import { Menu as HeadlessMenu } from '@headlessui/react';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { SearchIcon } from '../../icons';
import { Input } from '../Input';
import { Spinner } from '../Spinner';
import type { MenuItem } from './menu-types/menu-item';
import type { MenuProps } from './menu-types/menu-props';

export const Menu:React.FC<MenuProps> = ({
  children,
  menuItems,
  menuItemsWithHeaders,
  align = 'left',
  className: externalClassName = '',
  withSearch = false,
  isLoading,
  wrapperClassName,
  labelClassName,
  menuItemClassName,
}) => {
  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItem[] | undefined>(menuItems);

  useEffect(() => {
    setFilteredMenuItems(menuItems);
  }, [menuItems]);

  const getAlignStyles = () => {
    switch (align) {
      case 'right':
        return 'left-[calc(100%+1rem)] before:right-full before:rotate-180';
      default:
        return 'right-[calc(100%+1rem)] before:left-full';
    }
  };

  const onSearch = (e: any) => {
    if (Array.isArray(menuItems)) {
      const filteredItems = menuItems?.filter((item) => item.label?.toLowerCase().trim().indexOf(e.target.value?.toLowerCase()
        .trim()) > -1);
      setFilteredMenuItems(filteredItems);
    }
  };

  return (
    <HeadlessMenu
      as="div"
      className={twMerge('relative h-full w-fit text-left', wrapperClassName)}
    >
      <HeadlessMenu.Button
        className="h-full"
        as="div"
      >
        {children}
      </HeadlessMenu.Button>
      {filteredMenuItems ? (
        <HeadlessMenu.Items
          className={`absolute ${getAlignStyles()} rounded-2lg border-gray before:border-2lg before:border-l-gray top-1/2 z-10
              flex w-[20rem] -translate-y-1/2 flex-col border-2 bg-white
              py-2.5 outline-none before:absolute before:top-1/2 before:-translate-y-1/2 before:border-transparent before:content-[""] ${externalClassName}`}
        >
          {withSearch && (
            <div className="relative flex items-center">
              <span className="text-icon absolute left-2">
                <SearchIcon/>
              </span>
              <Input
                type="text"
                className="border-icon w-full rounded-md border bg-white py-2 pl-9 pr-4"
                placeholder="Search"
                onChange={onSearch}
              />
            </div>
          )}

          {isLoading && (
            <Spinner centerPage/>
          )}

          {filteredMenuItems.map((menuItem) => {
            return (
              <HeadlessMenu.Item
                key={menuItem.id ?? menuItem.label}
                as="p"
                className={classNames(twMerge('flex cursor-pointer items-center py-1.5 px-7 gap-2 hover:bg-input', menuItemClassName, menuItem?.className), { 'text-nav-item !cursor-default pointer-events-none': menuItem.disabled })}
                onClick={() => menuItem.onClick?.(menuItem.label, menuItem.id)}
                disabled={menuItem.disabled}
              >
                {menuItem.icon ?? <></>}
                <span className={twMerge('text-base', labelClassName)}>{menuItem.label}</span>
              </HeadlessMenu.Item>
            );
          })}
        </HeadlessMenu.Items>
      ) : <></>}
      {menuItemsWithHeaders ? (
        <HeadlessMenu.Items
          className={`absolute ${getAlignStyles()} rounded-2lg border-gray before:border-2lg before:border-l-gray top-1/2
              z-10 flex w-[20rem] -translate-y-1/2 flex-col border-2 bg-white
              pb-2.5 pt-5 outline-none before:absolute before:top-1/2 before:-translate-y-1/2 before:border-transparent before:content-[""] ${externalClassName}`}
        >
          {isLoading && (
            <Spinner centerPage/>
          )}
          {Object.keys(menuItemsWithHeaders).map((headerItem, index) => {
            return (
              <div
                key={headerItem}
                className={classNames(
                  { 'mb-3': index !== Object.keys(menuItemsWithHeaders).length - 1 },
                )}
              >
                <div className="text-nav-item mb-2.5 px-5 text-base">{headerItem}</div>
                {menuItemsWithHeaders?.[headerItem].map((menuItem) => {
                  return (
                    <HeadlessMenu.Item
                      key={menuItem.id ?? menuItem.label}
                      as="p"
                      className={classNames(twMerge('flex cursor-pointer items-center py-1.5 px-7 gap-2 hover:bg-input', menuItemClassName, menuItem?.className),
                        { 'text-nav-item !cursor-default pointer-events-none': menuItem.disabled })}
                      onClick={() => menuItem.onClick?.(menuItem.label, menuItem.id)}
                      disabled={menuItem.disabled}
                      data-cy={menuItem['data-cy']}
                    >
                      {menuItem.icon ?? <></>}
                      <span className={twMerge('text-base', labelClassName)}>{menuItem.label}</span>
                    </HeadlessMenu.Item>
                  );
                })}
              </div>
            );
          })}
        </HeadlessMenu.Items>
      ) : <></>}
    </HeadlessMenu>
  );
};